import { Injectable, inject } from '@angular/core';
import { of } from 'rxjs';
import { Params, QueryParamsHandling } from '@angular/router';
import { HtmlAnchorTarget, isNil, Nillable } from '@web/utils';
import { ActionButtonCommand } from '../types/action-button-command';
import { ActionButtonVisibility } from '../types/action-button-visibility';
import { ActionButtonDisability } from '../types/action-button-disability';
import { ActionButtonType } from '../enums/action-button-type';
import { ActionButtonVariant } from '../enums/action-button-variant';
import { ActionButton } from '../models/action-button';
import {
  RequiredMethodCallException,
  RouteProviderService,
  RouterLink,
} from '@web/common';

@Injectable({
  providedIn: 'root',
})
export class ActionButtonBuilder<Action> {
  private readonly provider = inject(RouteProviderService);

  private _key: Nillable<Action>;
  private _label: Nillable<string>;
  private _command: Nillable<ActionButtonCommand>;
  private _visibility: Nillable<ActionButtonVisibility>;
  private _disability: Nillable<ActionButtonDisability>;
  private _type: Nillable<ActionButtonType>;
  private _routerLink: Nillable<RouterLink>;
  private _url: Nillable<string>;
  private _target: Nillable<HtmlAnchorTarget>;
  private _queryParams: Nillable<Params>;
  private _queryParamsHandling: Nillable<QueryParamsHandling>;
  private _variant: Nillable<ActionButtonVariant>;

  public constructor() {
    this.reset();
  }

  public reset(): this {
    this._label = null;
    this._key = null;
    this._command = null;
    this._visibility = null;
    this._disability = null;
    this._type = null;
    this._routerLink = null;
    this._url = null;
    this._target = null;
    this._queryParams = null;
    this._queryParamsHandling = null;
    this._variant = null;

    return this;
  }

  public initCommand(key: Action): this {
    return this.key(key).type(ActionButtonType.Command);
  }

  public initRouterLink(key: Action): this {
    return this.key(key).type(ActionButtonType.RouterLink);
  }

  public initUrl(key: Action): this {
    return this.key(key).type(ActionButtonType.Url);
  }

  public routerLink(
    factory: (provider: RouteProviderService) => RouterLink,
  ): this {
    this._routerLink = factory(this.provider);

    return this;
  }

  public url(url: string): this {
    this._url = url;

    return this;
  }

  public key(key: Action): this {
    this._key = key;

    return this;
  }

  public type(type: ActionButtonType): this {
    this._type = type;

    return this;
  }

  public command(command: ActionButtonCommand): this {
    this._command = command;

    return this;
  }

  public label(label: string): this {
    this._label = label;

    return this;
  }

  public visibility(visibility: ActionButtonVisibility): this {
    this._visibility = visibility;

    return this;
  }

  public disability(disability: ActionButtonDisability): this {
    this._disability = disability;

    return this;
  }

  public target(target: HtmlAnchorTarget): this {
    this._target = target;

    return this;
  }

  public routerLinkQueryParams(queryParams: Params): this {
    this._queryParams = queryParams;

    return this;
  }

  public queryParamsHandling(queryParamsHandling: QueryParamsHandling): this {
    this._queryParamsHandling = queryParamsHandling;

    return this;
  }

  public variant(variant: ActionButtonVariant): this {
    this._variant = variant;

    return this;
  }

  public build(): ActionButton<Action> {
    this.requireLabel();
    this.requireKey();
    this.requireType();

    if (this.isCommandType()) {
      this.requireCommand();
    }

    if (this.isUrlType()) {
      this.requireUrl();
      this.configureDefaultTarget();
    }

    if (this.isRouterLinkType()) {
      this.requireRouterLink();
      this.configureDefaultTarget();
    }

    this.configureDefaultVariant();
    this.configureDefaultVisibility();
    this.configureDefaultDisability();

    const action = new ActionButton<Action>({
      key: this._key!,
      label: this._label!,
      visibility$: this._visibility!,
      disability$: this._disability!,
      type: this._type!,
      variant: this._variant!,
      command: this._command,
      routerLink: this._routerLink,
      url: this._url,
      target: this._target,
      queryParams: this._queryParams,
      queryParamsHandling: this._queryParamsHandling,
    });

    this.reset();

    return action;
  }

  private requireLabel(): void {
    if (!isNil(this._label)) {
      return;
    }

    throw new RequiredMethodCallException(this.label.name);
  }

  private requireKey(): void {
    if (!isNil(this._key)) {
      return;
    }

    throw new RequiredMethodCallException(this.key.name);
  }

  private requireCommand(): void {
    if (!isNil(this._command)) {
      return;
    }

    throw new RequiredMethodCallException(this.command.name);
  }

  private requireUrl(): void {
    if (!isNil(this._url)) {
      return;
    }

    throw new RequiredMethodCallException(this.url.name);
  }

  private requireRouterLink(): void {
    if (!isNil(this._routerLink)) {
      return;
    }

    throw new RequiredMethodCallException(this.routerLink.name);
  }

  private requireType(): void {
    if (!isNil(this._type)) {
      return;
    }

    throw new RequiredMethodCallException(this.type.name);
  }

  private configureDefaultVariant(): void {
    if (!isNil(this._variant)) {
      return;
    }

    this.variant(ActionButtonVariant.Secondary);
  }

  private configureDefaultVisibility(): void {
    if (!isNil(this._visibility)) {
      return;
    }

    this.visibility(() => of(true));
  }

  private configureDefaultDisability(): void {
    if (!isNil(this._disability)) {
      return;
    }

    this.disability(() => false);
  }

  private isCommandType(): boolean {
    return this._type === ActionButtonType.Command;
  }

  private isUrlType(): boolean {
    return this._type === ActionButtonType.Url;
  }

  private isRouterLinkType(): boolean {
    return this._type === ActionButtonType.RouterLink;
  }

  private configureDefaultTarget(): void {
    if (!isNil(this._target)) {
      return;
    }

    this.target(HtmlAnchorTarget.Self);
  }
}
